import type { ConsentSnapshot, ConsentState, CookieCategories } from './types';

export interface StateMachineOptions {
  policyVersion: string;
  bannerVersion: string;
  sessionId: string;
}

function buildExpiresAt(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 12);
  return d.toISOString();
}

function buildSnapshot(
  options: StateMachineOptions,
  state: ConsentState,
  categories: CookieCategories,
  consentMethod: 'accept_all' | 'essential_only' | 'custom'
): ConsentSnapshot {
  const now = new Date().toISOString();
  return {
    sessionId: options.sessionId,
    state,
    categories,
    consentMethod,
    policyVersion: options.policyVersion,
    bannerVersion: options.bannerVersion,
    timestamp: now,
    expiresAt: buildExpiresAt(),
  };
}

export interface StateMachine {
  acceptAll(): ConsentSnapshot;
  essentialOnly(): ConsentSnapshot;
  setCustom(categories: { analytics: boolean; marketing: boolean }): ConsentSnapshot;
  isExpired(): boolean;
  isExpiredSnapshot(snapshot: ConsentSnapshot): boolean;
  getCurrent(): ConsentSnapshot | null;
}

export function createStateMachine(options: StateMachineOptions): StateMachine {
  let current: ConsentSnapshot | null = null;

  function isExpiredSnapshot(snapshot: ConsentSnapshot): boolean {
    const now = Date.now();
    const expiresAt = new Date(snapshot.expiresAt).getTime();
    if (expiresAt <= now) {
      return true;
    }
    // Version mismatch = expired (re-prompt required)
    if (snapshot.policyVersion !== options.policyVersion) {
      return true;
    }
    if (snapshot.bannerVersion !== options.bannerVersion) {
      return true;
    }
    return false;
  }

  return {
    acceptAll(): ConsentSnapshot {
      const snapshot = buildSnapshot(
        options,
        'ACCEPT_ALL',
        { essential: true, analytics: true, marketing: true },
        'accept_all'
      );
      current = snapshot;
      return snapshot;
    },

    essentialOnly(): ConsentSnapshot {
      const snapshot = buildSnapshot(
        options,
        'ESSENTIAL_ONLY',
        { essential: true, analytics: false, marketing: false },
        'essential_only'
      );
      current = snapshot;
      return snapshot;
    },

    setCustom(categories: { analytics: boolean; marketing: boolean }): ConsentSnapshot {
      const snapshot = buildSnapshot(
        options,
        'CUSTOM',
        { essential: true, ...categories },
        'custom'
      );
      current = snapshot;
      return snapshot;
    },

    isExpired(): boolean {
      if (!current) return false;
      return isExpiredSnapshot(current);
    },

    isExpiredSnapshot,

    getCurrent(): ConsentSnapshot | null {
      return current;
    },
  };
}
